import { Platform } from "react-native";

export type ResolvedAppLink = {
  href: string;
  openInApp: boolean;
};

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

// Native has no window.location, so full app URLs are recognized as deep links
// via this list. Extend it via EXPO_PUBLIC_APP_HOSTS (comma-separated).
const KNOWN_APP_HOSTS = ["app.openstudiojazz.com"];

function envAppHosts(): string[] {
  const raw = process.env.EXPO_PUBLIC_APP_HOSTS;
  if (typeof raw !== "string" || !raw.trim()) return [];
  return raw
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

function appHostnames(): Set<string> {
  const hosts = new Set<string>([...KNOWN_APP_HOSTS, ...envAppHosts()]);

  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    window.location?.hostname
  ) {
    hosts.add(window.location.hostname.toLowerCase());
  }

  return hosts;
}

function isAppHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (LOCAL_HOSTS.has(h)) return true;
  if (h.endsWith(".vercel.app")) return true;
  return appHostnames().has(h);
}

/**
 * Decide whether a link stays in the app or opens externally, based on its
 * origin rather than its shape. Same-origin URLs are normalized to a path and
 * kept in-app; any other host opens externally.
 */
export function resolveAppLink(raw: string | null | undefined): ResolvedAppLink {
  const value = (raw ?? "").trim();
  if (!value || value === "#") return { href: value || "#", openInApp: false };

  const candidate = value.startsWith("//") ? `https:${value}` : value;

  if (candidate.startsWith("/")) {
    return { href: candidate, openInApp: true };
  }

  if (/^https?:\/\//i.test(candidate)) {
    try {
      const u = new URL(candidate);
      if (isAppHost(u.hostname)) {
        return { href: u.pathname + u.search + u.hash, openInApp: true };
      }
      return { href: u.href, openInApp: false };
    } catch {
      return { href: candidate, openInApp: false };
    }
  }

  // Non-http scheme (mailto:, tel:, …).
  if (/^[a-z][a-z0-9+.-]*:/i.test(candidate)) {
    return { href: candidate, openInApp: false };
  }

  // Bare domain (e.g. "youtube.com/watch").
  const firstSegment = candidate.split("/")[0];
  if (firstSegment.includes(".")) {
    try {
      const u = new URL(`https://${candidate}`);
      if (isAppHost(u.hostname)) {
        return { href: u.pathname + u.search + u.hash, openInApp: true };
      }
      return { href: u.href, openInApp: false };
    } catch {
      // fall through
    }
  }

  return { href: `/${candidate.replace(/^\/+/, "")}`, openInApp: true };
}

/**
 * In-app route for an ABSOLUTE http(s) URL on an app host (e.g. a lesson link
 * pasted into The Hang), or null for anything else. Unlike resolveAppLink()
 * this never treats bare paths or bare domains as in-app, so it is safe to run
 * on raw navigation URLs coming out of a WebView.
 */
export function appRouteForAbsoluteUrl(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!/^https?:\/\//i.test(value)) return null;
  const { href, openInApp } = resolveAppLink(value);
  return openInApp ? href : null;
}
